/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-unused-expressions */
// node modules
import debounce from 'lodash/debounce';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
} from 'react';
import { observer } from 'mobx-react';
import 'assets/rotate-light-tv.svg';
// modules
import Logger from 'modules/Logger';
import Format from 'modules/Format';
import { edisStore } from 'modules/Edis';
import PlatformService from 'modules/Platform';
import { CookieAttributes } from 'js-cookie';
// ui library
import { View, Loading } from '@upstox/upstox-ui';
import useThemeDetector from '@upstox/upstox-ui/component/hook/useThemeDetector';
// libraries
import TradingChartService from 'modules/TradingChart/service';
import {
  INTERVAL_TYPE,
  OptionChainModalMinWidth,
  ServerIntervals,
  UPSTOX_LAYOUT_NAME,
  broker,
  durations,
} from 'modules/TradingChart/consts';
import Loader from 'modules/TradesPW3/Orders/Loader';
import { useStore } from 'modules/Stores';
import { ITVPosition } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/positions/types';
import WatchlistService from 'modules/Watchlists';
import SearchService from 'modules/Search';
import AppService, { ReleaseNotesIndicatorStates } from 'modules/App';
import OptionChainService from 'modules/OptionChain';
import AnalyticsService from 'modules/Analytics/service';
import PreRiskService from 'modules/PreRisk';
import UnderlyingChartsService from 'modules/UnderlyingCharts';
import { CookieStorage, LStorage } from 'modules/Storage';
import { EVENTS } from 'modules/Analytics/consts';
import LocaleService from 'modules/Localization';
import ValidationService from 'modules/Validation';
import Settings from 'modules/Settings';
// components
import AveragePriceUpdateNotificationFooter from 'modules/TradingChart/components/TradingView/components/AveragePriceUpdateNotificationFooter';
import {
  decomposeExchangeToken,
  decomposeInstrumentKey,
} from 'modules/BOD/modules/Search/utils';
import { SegmentType } from 'modules/Search/constants';
import { SAVED_WATCHLIST_SYMBOLS } from 'modules/Watchlists/constants';
import {
  useCustomModalOpen,
  usePrevious,
  usePriceAlertsInChart,
  useUnderlyingCharts,
} from 'utils/hooks';
import { TimeIntervals } from '../typings';
import { ChartContainerProps } from './types';
import { IUpdatedBrokerConnectionAdapterHost } from '../utils/providers/brokerProvider/typings';
import {
  TradingTerminalWidgetOptions,
  IChartingLibraryWidget,
  widget as Widget,
  ContextMenuItem,
  IChartWidgetApi,
  ResolutionString,
  IBrokerWithoutRealtime,
  IDropdownApi,
  StudyOrDrawingAddedToChartEventParams,
  TradingTerminalFeatureset,
  IActionVariant,
  ActionsFactory,
  CreateContextMenuParams,
  CrossHairMovedEventParams,
  SaveLoadChartRecord,
} from '../sources/charting_library.min';
// configs
import {
  getActionsButtons,
  getBaseWidgetOptions,
  getChartAttributes,
} from '../configs';
// utils
import { DataProvider } from '../utils/providers/chartsData';
import Broker from '../utils/providers/brokerProvider';
import ChartFormatService from '../utils/services/format';
import SaveLoadLayoutAdapter from '../utils/providers/saveLoad';
// css
import * as css from './styles';
// consts
import { CHART_TO_USER_INTERVAL } from '../utils/services/format/format';
import { TOOLBAR_OBSERVER_CONFIG } from './constants';
import { ActionId, NotificationType } from '../sources/charting_library';
import ChartDocumentTitle from './ChartDocumentTitle';
import UpdatePositionPnl from './UpdatePositionPnl/UpdatePositionPnl';
import useCustomDOMWidgetButton from '../hooks/useCustomDOMWidgetButton';
// utils
import { addSingleScripSurveillanceEvent } from './SurveillanceMessages/utils';
import { customIndicators } from './customIndicators';

let saveLoadLayoutAdapter: SaveLoadLayoutAdapter;

const TradingViewChart: React.FC<ChartContainerProps> = ({
  symbol,
  interval,
  onIntervalChanged,
  handleSymbolSelect,
  barsProviderService,
  symbolsSearchService,
  barsProviderRest,
  handleAddFunds,
  handleLogout,
  handleHelp,
  isPopup,
  isFromPW3,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [showLoading, setLoadingState] = useState(true);
  const [isWidgetReady, setWidgetReady] = useState(false);
  const [, setForceUpdatePositionPnl] = useState(false);

  const {
    toggleModal,
    isChartLayoutUpdated,
    setIsChartLayoutUpdated,
    isOffline,
    releaseNotesIndicatorState,
  } = useStore('AppStore');
  const {
    openConvertPositionModal,
    setClosingPositionId,
    setSurveillanceAlertAccepted,
    isPositionConvertible,
  } = useStore('PositionsPW3Service');
  const { profile } = useStore('UserStore');
  const { goToRoute } = useStore('RoutingStore');
  const {
    reconnected: liveV2Reconnected,
    connected: liveV2Connected,
  } = useStore('WsLiveV2Feeder');
  const {
    setOptionChainData,
    clear: clearOptionData,
    setIsMinimised,
    unsubscribeOptionChainScrips,
  } = useStore('OptionChainStore');
  const { setScripData, setModalType, setPriceAbove, setPriceBelow } = useStore(
    'PriceAlertsStore',
  );
  const {
    openChartLayoutListModal,
    setOpenChartLayoutListModal,
    chartSymbol,
    toggleContinousChart,
    isContinousChartChecked,
    setContinousChartInstrumentKey,
    setIsContinuousChartsApplicable,
    isContinuousChartsApplicable,
    setIsContinousChart,
    setChartIntervalByExchangeToken,
    chartIntervalByExchangeToken,
    setChartSymbol,
    reloadLayout,
    currentLayoutId,
    upstoxLayoutId,
    removedDrawingSourceIds,
  } = useStore('TradingChartStore');
  const {
    isUnderlyingChartsApplicable,
    isUnderlyingChartsChecked,
    setIsUnderlyingChartsApplicable,
  } = useStore('UnderlyingChartsStore');

  const continuousChartToken = `${symbol}_${chartIntervalByExchangeToken[symbol]}`;

  const isContinousChartCheckedForSymbol =
    isContinousChartChecked[continuousChartToken];

  const isContinuousChartsApplicableForSymbol =
    isContinuousChartsApplicable[symbol];

  const isUnderlyingChartsApplicableForSymbol =
    isUnderlyingChartsApplicable[symbol];

  const { getLoading } = useStore('LoadingStore');

  const { syncingInProgress } = useStore('ChartSyncStore');

  const { priceAlertList: priceAlertListData } = useStore('PriceAlertsStore');

  const isChartsLoading = getLoading('CHARTS_DATA');

  const {
    loadings: { APP_INIT: isAppLoading },
  } = useStore('LoadingStore');

  const { userPreferenceSettings } = useStore('UserStore');
  const saveChartsToServer = userPreferenceSettings?.saveChartsToServer;
  const autoSaveCharts = Settings.getIsAutoSaveChartsEnabled();

  const symbolChangeStartTime = useRef(0);
  const intervalChangeStartTime = useRef(0);
  const crossHairPrice = useRef('');

  const addFundsButton = useRef<HTMLElement | null>(null);
  const optionChainButton = useRef<HTMLElement | null>(null);
  const switchTabButton = useRef<HTMLElement | null>(null);
  const menuButton = useRef<IDropdownApi | null>(null);
  const tvWidget = useRef<IChartingLibraryWidget | null>(null);
  const activeChart = useRef<IChartWidgetApi | undefined>();
  const changeThemeTimeout = useRef<ReturnType<typeof setTimeout> | null>();
  const tbtFooterButton = useRef<HTMLElement | null>(null);
  const tvChartSyncButton = useRef<HTMLElement | null>(null);
  const positionPnlElement = useRef<HTMLElement | null>(null);
  const footerButtonTimeout = useRef<ReturnType<typeof setTimeout> | null>();
  const continuousChartsButton = useRef<HTMLElement | null>(null);
  const priceAlertButton = useRef<HTMLElement | null>(null);
  const underlyingChartsButton = useRef<HTMLElement | null>(null);
  const isUnderlyingChartsCheckedRef = useRef(false);
  isUnderlyingChartsCheckedRef.current = isUnderlyingChartsChecked;

  const saveChartsToServerRef = useRef(false);
  const isOfflineRef = useRef(false);
  // eslint-disable-next-line max-len
  const prevSaveChartsToServer = usePrevious(saveChartsToServer);

  const shouldResetChart = !isOffline && (liveV2Reconnected || liveV2Connected);

  const prevShouldResetChart = usePrevious(shouldResetChart);

  const toolbarObserver = useRef<MutationObserver | null>(null);
  const { createPriceAlert } = usePriceAlertsInChart();
  const {
    createUnderlyingCharts,
    handleUnderlyingChartsOnLayoutChange,
    removeAllExistingUnderlyingCharts,
  } = useUnderlyingCharts();

  const { theme, prevTheme, setTheme, isOrderReviewEnabled } = useStore(
    'AppStore',
  );
  const autoTheme = useThemeDetector();
  const currentTheme = theme === 'auto' ? autoTheme : theme;

  const digitsAfterDot = Format.getDigitsAfterDot(symbol);

  const tradingviewSymboleditFilter = localStorage.getItem(
    'tradingview.symboledit.filter',
  );

  const cookieConfig: CookieAttributes = {
    domain: 'upstox.com',
    sameSite: 'Strict',
  };

  const isDarkTheme = currentTheme === 'dark';
  const showOrderReview = JSON.parse(isOrderReviewEnabled);

  const { baseStyle, popoutStyle } = getChartAttributes();

  const {
    domButtonSvg,
    addFundsButtonSvg,
    menuButtonElement,
    switchTabButtonElement,
    tbtFooterButtonSvg,
    continuousChartsUncheckedButton,
    continuousChartsCheckedButton,
    underlyingChartsActiveButton,
    underlyingChartsInactiveButton,
    getOptionChainButton,
    getPriceAlertButton,
    getReleaseNotesButtonSvg,
    communityButtonSvg,
  } = getActionsButtons();
  const releaseNotesButtonSvg = getReleaseNotesButtonSvg(
    releaseNotesIndicatorState === ReleaseNotesIndicatorStates.NEW_INTIMATION,
  );

  const {
    rightToolbarDOMButton,
    rightToolbarReleaseNotesButton,
    rightToolbarCommunityButton,
    handleRightToolbarDOMButton,
  } = useCustomDOMWidgetButton(
    domButtonSvg,
    getReleaseNotesButtonSvg,
    communityButtonSvg,
  );

  const isCustomModalOpen = useCustomModalOpen();

  useEffect(() => {
    // custom modal never appers in full screen - exit from full screen
    if (isCustomModalOpen && tvWidget.current) {
      tvWidget.current.exitFullscreen();
    }
  }, [isCustomModalOpen]);

  const getName = useCallback(() => {
    if (profile) {
      const name = `${profile.firstName} ${profile.middleName} ${profile.lastName}`;
      const names = name.split(' ');
      const firstName = names[0];
      const lastName = names[names.length - 1];
      return `${firstName[0]}${firstName.slice(1).toLocaleLowerCase()} ${
        lastName ? `${lastName[0]}.` : ''
      }`;
    }
    return '';
  }, [profile]);

  const studyHandler = (indicator: StudyOrDrawingAddedToChartEventParams) => {
    AnalyticsService.sendEvent(EVENTS.CHART_ADDED_INDICATOR, {
      source: 'TV',
      name: indicator.value,
    });
  };

  const getChartInterval = useCallback(
    (): ResolutionString =>
      tvWidget.current?.activeChart().resolution() || ('1' as ResolutionString),
    [],
  );

  const resetActiveChart = useCallback(async () => {
    if (tvWidget?.current) {
      const currentResolution = getChartInterval();
      const mappedInterval: string = ChartFormatService.mapTVChartInterval(
        currentResolution,
      );
      const scripData = await SearchService.searchByExchangeToken(symbol);
      TradingChartService.resetActiveChart(scripData?.exchangeToken || '', mappedInterval);
      const chartsCount = tvWidget.current?.chartsCount();

      const activeChartExchangeToken = getExchangeToken();

      for (let index = 0; index < chartsCount; index++) {
        const currentChartSymbol = tvWidget.current.chart(index).symbol();
        const chartExchangeToken = Format.decodeCustomTvExchangeToken(
          currentChartSymbol || '',
        );
        // reset all charts having same exchange token and interval as active chart
        if (chartExchangeToken === activeChartExchangeToken) {
          tvWidget.current.chart(index).resetData();
        }
      }
    }
  }, [symbol, tvWidget, getChartInterval]);

  const sendMixpanelEventForContinuousCharts = () => {
    const activeChartSymbol =
      tvWidget.current?.activeChart()?.symbolExt()?.symbol || '';
    AnalyticsService.sendEvent(EVENTS.CONTINUOUS_CHARTS_ENABLED, {
      Symbol: activeChartSymbol,
    });
  };

  const handleContinuousCharts = useCallback(() => {
    if (tvWidget.current && broker.instance) {
      if (!isContinousChartCheckedForSymbol) {
        sendMixpanelEventForContinuousCharts();
      }
      const activeChartInterval = getChartInterval();
      const mappedInterval = ChartFormatService.mapTVChartInterval(
        activeChartInterval,
      );
      let token = `${symbol}_${mappedInterval}`;
      if (mappedInterval !== ServerIntervals.ONE_DAY) {
        tvWidget.current
        .activeChart()
        .setResolution(ServerIntervals.ONE_DAY as ResolutionString);
        token = `${symbol}_1d`;

        if (isContinousChartChecked[token]) {
          return;
        }
      }

      toggleContinousChart(token);

      resetActiveChart();
    }
  }, [
    isContinousChartCheckedForSymbol,
    toggleContinousChart,
    symbol,
    resetActiveChart,
    getChartInterval,
    isContinousChartChecked,
  ]);

  const handleUnderlyingCharts = useCallback(() => {
    if (tvWidget.current && broker.instance) {
      if (isUnderlyingChartsChecked) {
        removeAllExistingUnderlyingCharts(tvWidget.current);
      } else {
        AnalyticsService.sendEvent(EVENTS.ICON_CLICKED, {
          'Icon Name': 'Underlying Charts',
        });
      }
      UnderlyingChartsService.toggleUnderlyingCharts();
      resetActiveChart();
    }
  }, [
    isUnderlyingChartsChecked,
    resetActiveChart,
    removeAllExistingUnderlyingCharts,
  ]);

  // Note: To change chart symbol from external source
  useEffect(() => {
    const setActiveChartSymbol = async () => {
      if (tvWidget.current && isWidgetReady && chartSymbol) {
        const exchangeToken = getExchangeToken();
        // don't set for same chart symbol
        if (exchangeToken === chartSymbol) {
          return;
        }
        const scripData = await SearchService
          .searchByExchangeToken(chartSymbol);
        const tvExchangeToken = Format.generateCustomTvExchangeToken(
          scripData?.displayName || '',
          scripData?.exchangeToken || '',
        );
        tvWidget.current
          .setSymbol(tvExchangeToken, getChartInterval(), () => ({}));
      }
    };
    setActiveChartSymbol();
  }, [chartSymbol, getChartInterval, isWidgetReady]);

  useEffect(() => {
    if (tradingviewSymboleditFilter === null) {
      localStorage.setItem('tradingview.symboledit.filter', 'ALL');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile && isInitialized && menuButton.current) {
      menuButton.current.applyOptions({
        icon: menuButtonElement(getName()),
      });
    }
  }, [profile, menuButton, getName, isInitialized, menuButtonElement]);

  useEffect(() => {
    const hasThemeChanged = prevTheme !== currentTheme;

    if (
      isInitialized &&
      tvWidget.current &&
      isWidgetReady &&
      (hasThemeChanged || isChartLayoutUpdated)
    ) {
      if (hasThemeChanged) {
        setTheme(theme, currentTheme);
      }
      if (changeThemeTimeout.current) {
        clearTimeout(changeThemeTimeout.current);
      }
      changeThemeTimeout.current = setTimeout(() => {
        if (tvWidget.current) {
          tvWidget.current
            .changeTheme(isDarkTheme ? 'dark' : 'light')
            .then(() => {
              if (addFundsButton.current) {
                addFundsButton.current.innerHTML = addFundsButtonSvg(
                  isDarkTheme,
                );
              }
              if (optionChainButton.current) {
                optionChainButton.current.innerHTML = getOptionChainButton();
              }
              if (rightToolbarDOMButton.current) {
                rightToolbarDOMButton.current.innerHTML = domButtonSvg;
              }
              if (rightToolbarReleaseNotesButton.current) {
                rightToolbarReleaseNotesButton.current.innerHTML =
                  releaseNotesButtonSvg;
              }
              if (rightToolbarCommunityButton.current) {
                rightToolbarCommunityButton.current.innerHTML =
                  communityButtonSvg;
              }
            });
        }
      }, 0);
      if (isChartLayoutUpdated) {
        setIsChartLayoutUpdated(false);
      }
    }
  }, [
    theme,
    setTheme,
    prevTheme,
    isDarkTheme,
    currentTheme,
    isWidgetReady,
    isInitialized,
    domButtonSvg,
    addFundsButtonSvg,
    releaseNotesButtonSvg,
    communityButtonSvg,
    isChartLayoutUpdated,
    setIsChartLayoutUpdated,
    tbtFooterButtonSvg,
    getOptionChainButton,
    rightToolbarDOMButton,
    rightToolbarReleaseNotesButton,
    rightToolbarCommunityButton,
  ]);

  useEffect(() => {
    if (continuousChartsButton?.current) {
      const activeChartInterval = getChartInterval();
      const tvChartInterval = ChartFormatService.mapTVChartInterval(
        activeChartInterval,
      );
      const isDisabled =
        isChartsLoading ||
        !isContinuousChartsApplicableForSymbol ||
        tvChartInterval !== ServerIntervals.ONE_DAY;
      continuousChartsButton.current.innerHTML =
        isContinousChartCheckedForSymbol
          ? continuousChartsCheckedButton(isDisabled)
          : continuousChartsUncheckedButton(isDisabled);

      const dataId = isDisabled
        ? 'ContinuousFuturesDisabled'
        : 'ContinuousFutures';

      continuousChartsButton.current.setAttribute('data-id', dataId);
    }
  }, [
    isContinousChartCheckedForSymbol,
    isChartsLoading,
    isContinuousChartsApplicable,
    continuousChartsCheckedButton,
    continuousChartsUncheckedButton,
    isContinuousChartsApplicableForSymbol,
    getChartInterval,
  ]);

  useEffect(() => {
    if (underlyingChartsButton.current) {
      const isDisabled =
        isChartsLoading ||
        !isUnderlyingChartsApplicableForSymbol;

      underlyingChartsButton.current.innerHTML =
        isUnderlyingChartsChecked &&
          isUnderlyingChartsApplicableForSymbol
          ? underlyingChartsActiveButton()
          : underlyingChartsInactiveButton(isDisabled);

      const dataId = isDisabled
        ? 'UnderlyingChartsDisabled'
        : 'UnderlyingCharts';

      underlyingChartsButton.current.setAttribute('data-id', dataId);
    }
  }, [
    isChartsLoading,
    isUnderlyingChartsApplicableForSymbol,
    isUnderlyingChartsChecked,
    underlyingChartsActiveButton,
    underlyingChartsInactiveButton,
  ]);

  useEffect(() => {
    if (tvWidget.current && isUnderlyingChartsChecked) {
      createUnderlyingCharts(tvWidget.current);
    }
  }, [
    createUnderlyingCharts,
    isUnderlyingChartsChecked,
    chartSymbol,
  ]);

  useEffect(() => {
    const activeChartInterval = getChartInterval();
    const tvChartInterval = ChartFormatService.mapTVChartInterval(
      activeChartInterval,
    );
    let hoverText = 'Continuous Futures';
    if (!isContinuousChartsApplicableForSymbol) {
      hoverText = 'Available only for NSE futures contract charts';
    } else if (tvChartInterval !== ServerIntervals.ONE_DAY) {
      hoverText = 'Available only for daily, weekly and monthly timeframes';
    }
    if (continuousChartsButton.current) {
      continuousChartsButton.current.setAttribute('title', hoverText);
    }
  }, [isContinuousChartsApplicableForSymbol, getChartInterval, interval]);

  useEffect(() => {
    const activeChartInterval = getChartInterval();
    const tvChartInterval = ChartFormatService.mapTVChartInterval(
      activeChartInterval,
    );
    if (continuousChartsButton?.current) {
      continuousChartsButton.current.classList.remove('disabled');
      continuousChartsButton.current.removeEventListener(
        'click',
        handleContinuousCharts,
      );
      const isContinuousChartsApplicableForSymbolAndInterval =
        isContinuousChartsApplicableForSymbol &&
          tvChartInterval === ServerIntervals.ONE_DAY;

      if (isContinuousChartsApplicableForSymbolAndInterval) {
        continuousChartsButton.current.addEventListener(
          'click',
          handleContinuousCharts,
        );
      } else {
        continuousChartsButton.current.classList.add('disabled');
      }
    }

    return () => {
      if (continuousChartsButton?.current) {
        continuousChartsButton.current.removeEventListener(
          'click',
          handleContinuousCharts,
        );
      }
    };
  }, [
    isContinuousChartsApplicableForSymbol,
    handleContinuousCharts,
    getChartInterval,
  ]);

  useEffect(() => {
    if (underlyingChartsButton.current) {
      underlyingChartsButton.current.classList.remove('disabled');
      underlyingChartsButton.current.removeEventListener(
        'click',
        handleUnderlyingCharts,
      );

      let hoverText = LocaleService.translate('UNDERLYING_CHARTS');
      if (isUnderlyingChartsApplicableForSymbol) {
        underlyingChartsButton.current.addEventListener(
          'click',
          handleUnderlyingCharts,
        );
      } else {
        underlyingChartsButton.current.classList.add('disabled');
        hoverText = LocaleService.translate('UNDERLYING_CHARTS_DISABLED_MESSAGE');
      }
      underlyingChartsButton.current.setAttribute('title', hoverText);
    }

    return () => {
      if (underlyingChartsButton.current) {
        underlyingChartsButton.current.removeEventListener(
          'click',
          handleUnderlyingCharts,
        );
      }
    };
  }, [isUnderlyingChartsApplicableForSymbol, handleUnderlyingCharts]);

  useEffect(() => {
    if (broker.instance) {
      broker.instance.patchConfig({
        supportPlaceOrderPreview: showOrderReview,
        supportModifyOrderPreview: showOrderReview,
      });
    }
  }, [showOrderReview]);

  useEffect(() => {
    if (tvWidget.current && isWidgetReady && openChartLayoutListModal) {
      // eslint-disable-next-line no-unused-expressions
      tvWidget.current?.showLoadChartDialog?.();
      setOpenChartLayoutListModal(false);
    }
  }, [openChartLayoutListModal, setOpenChartLayoutListModal, isWidgetReady]);

  useEffect(() => {
    TradingChartService.setChartActiveSymbolData(symbol);
  }, [symbol]);

  useLayoutEffect(() => {
    // Need for clear watchlists from localStorage
    window.onbeforeunload = () => {
      const localKeys = Object.keys(window.localStorage);
      const filteredKeys = localKeys.filter(key =>
        key.includes(SAVED_WATCHLIST_SYMBOLS),
      );

      for (let i = 0; i < filteredKeys.length; i++) {
        window.localStorage.removeItem(filteredKeys[i]);
      }
    };

    if (!saveLoadLayoutAdapter) {
      saveLoadLayoutAdapter = new SaveLoadLayoutAdapter(symbol);
    } else {
      saveLoadLayoutAdapter.exchangeToken = symbol;
    }
  }, [symbol]);

  const getWidgetOptions = useCallback((): TradingTerminalWidgetOptions => {
    let disabledFeatures: TradingTerminalFeatureset[] = [
      'order_info',
      'uppercase_instrument_names',
      'drawing_templates',
    ];
    let enabledFeatures: TradingTerminalFeatureset[] = [
      'study_templates',
      'dom_widget',
      'order_panel',
      'show_average_close_price_line_and_label',
      'show_percent_option_for_right_margin',
      'use_last_visible_bar_value_in_legend',
      'header_in_fullscreen_mode',
      'hide_price_scale_if_all_sources_hidden',
      'always_show_legend_values_on_mobile',
      'saveload_separate_drawings_storage',
      'custom_resolutions',
      'chart_style_hilo_last_price',
    ];

    if (isPopup) {
      disabledFeatures = [
        ...disabledFeatures,
        'trading_account_manager',
        'show_right_widgets_panel_by_default',
      ];
    } else {
      enabledFeatures = [
        ...enabledFeatures,
        'show_right_widgets_panel_by_default',
      ];
    }

    const baseWidgetOptions = getBaseWidgetOptions({
      digitsAfterDot,
      theme: currentTheme,
      saveChartsToServer,
    });
    const datafeed = new DataProvider({
      symbolSelectCallback: handleSymbolSelect,
      barsProviderService,
      symbolsSearchService,
      barsProviderRest,
      isSymbolsSearchAvailable: true,
    });

    return {
      ...baseWidgetOptions,
      debug: false,
      widgetbar: {
        ...baseWidgetOptions.widgetbar,
        ...(isPopup && {
          details: false,
          watchlist: false,
          datawindow: false,
        }),
      },
      symbol,
      datafeed,
      interval: ChartFormatService.getChartIntervalByUserPeriod(
        interval,
      ) as ResolutionString,
      disabled_features: disabledFeatures,
      enabled_features: enabledFeatures,
      save_load_adapter: saveLoadLayoutAdapter,
      broker_factory: host => {
        const brokerInstance = new Broker(
          (host as unknown) as IUpdatedBrokerConnectionAdapterHost,
        );
        broker.instance = brokerInstance;
        return (brokerInstance as unknown) as IBrokerWithoutRealtime;
      },
      custom_indicators_getter: (pineJS) => customIndicators(pineJS),
      broker_config: {
        ...baseWidgetOptions.broker_config,
        // @ts-ignore
        durations,
        customUI: {
          async showClosePositionDialog(position) {
            let accepted: boolean | undefined = true;
            const scripData = await SearchService.searchByExchangeToken(
              position.exchangeToken,
            );
            const instrumentKey = scripData?.instrumentKey;
            const preRiskWarningMsg = await
              PreRiskService.getPreRiskWarningForSingleScrip(
                instrumentKey || '',
              );

            if (preRiskWarningMsg) {
              accepted = await broker.instance?.showConfirmationDialog(
                LocaleService.translate('SURVEILLANCE_WARNING'),
                preRiskWarningMsg,
                LocaleService.translate('PRERISK_CONTINUE'),
                LocaleService.translate('CANCEL_BUTTON'),
                false,
              );

              addSingleScripSurveillanceEvent(
                scripData,
                accepted,
                'Multi position square off',
              );
            }
            if (accepted) {
              const realPosition = (position as unknown) as ITVPosition;
              setClosingPositionId(realPosition.id);
              setSurveillanceAlertAccepted(
                realPosition.exchangeToken,
                !!preRiskWarningMsg,
              );
              toggleModal('closePosition');
            }

            return Promise.resolve(false);
          },
        },
        configFlags: {
          ...baseWidgetOptions.broker_config.configFlags,
          supportPlaceOrderPreview: showOrderReview,
          supportModifyOrderPreview: showOrderReview,
        },
      },
      context_menu: {
        items_processor(
          items: readonly IActionVariant[],
          actionsFactory: ActionsFactory,
          params: CreateContextMenuParams,
        ) {
          if (params.menuName === 'CrosshairMenuView') {
            const currentPrice = `${crossHairPrice.current}`;
            const createAlert = actionsFactory.createAction({
              actionId: ActionId.ChartCustomActionId,
              label: 'Create Price Alert',
              subItems: [
                actionsFactory.createAction({
                  actionId: ActionId.ChartCustomActionId,
                  label: `Above ${currentPrice}`,
                  onExecute: () => {
                    onPriceAlertsClick(true, currentPrice, 'Above');
                  },
                }),
                actionsFactory.createAction({
                  actionId: ActionId.ChartCustomActionId,
                  label: `Below ${currentPrice}`,
                  onExecute: () => {
                    onPriceAlertsClick(true, currentPrice, 'Below');
                  },
                }),
              ],
            });

            return Promise.resolve([...items, createAlert]);
          }

          if (params.menuName === 'TradingOrderContextMenu' && params.detail?.type === 'position') {
            let contextMenuItems = items;
            const positionId = params.detail?.id;
            if (positionId) {
              const isConvertPositionAvailable =
                isPositionConvertible(positionId);
              if (isConvertPositionAvailable) {
                const convertPosition = actionsFactory.createAction({
                  actionId: ActionId.ChartCustomActionId,
                  label: 'Convert Position',
                  onExecute: () => {
                    openConvertPositionModal(positionId);
                  },
                });
                const separatorIndex = items.findIndex(
                  item => item.type === 'separator',
                );
                contextMenuItems = [
                  ...items.slice(0, separatorIndex),
                  convertPosition,
                  ...items.slice(separatorIndex),
                ];
              }
            }
            return Promise.resolve(contextMenuItems);
          }

          return Promise.resolve(items);
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleSymbolSelect,
    interval,
    symbol,
    saveChartsToServer,
  ]);

  const onAutoSaveNeededHandler = useCallback(() => {
    tvWidget.current?.saveChartToServer();
  }, []);

  useEffect(() => {
    if (isInitialized && isWidgetReady && tvWidget?.current) {
      try {
        if (autoSaveCharts) {
          tvWidget.current.subscribe(
            'onAutoSaveNeeded',
            onAutoSaveNeededHandler,
          );
        } else {
          tvWidget.current.unsubscribe(
            'onAutoSaveNeeded',
            onAutoSaveNeededHandler,
          );
        }
      } catch (error) {
        // subscribe/unsubscribe error
      }
    }
  }, [autoSaveCharts, isInitialized, onAutoSaveNeededHandler, isWidgetReady]);

  useEffect(() => {
    // If the TradingView widget is initialized and there are drawings to be removed,
    // iterate through the `removedDrawingSourceIds` list and remove them from the charts.
    if (
      isInitialized &&
      tvWidget.current &&
      removedDrawingSourceIds?.length > 0
    ) {
      // Get the total number of charts in the multi-chart layout.
      const chartsCount = tvWidget.current?.chartsCount();
      // Iterate through the `removedDrawingSourceIds` list
      for (let i = 0; i < removedDrawingSourceIds.length; i++) {
        const removedEntityId = removedDrawingSourceIds[i];
        // Iterate through all the available charts to check if the entity exists
        for (let index = 0; index < chartsCount; index++) {
          const chart = tvWidget.current.chart(index);
          // Retrieve the sources (drawings) present in the chart.
          const { sources } = chart.getLineToolsState();
          // If the removed entity exists in the chart, remove it
          if (sources && sources.get(removedEntityId)) {
            tvWidget.current.activeChart().removeEntity(removedEntityId);
            break;
          }
        }
      }
    }
  }, [removedDrawingSourceIds, isInitialized]);

  const addVolumeIndicatorOnUpstoxLayout = () => {
    if (tvWidget.current) {
      if (LStorage.getItem('VOLUME_INDICATOR_ADDED')) {
        return;
      }
      if (tvWidget.current.layoutName() === UPSTOX_LAYOUT_NAME) {
        const chartsCount = tvWidget.current?.chartsCount();
        for (let chartIndex = 0; chartIndex < chartsCount; chartIndex++) {
          const chart = tvWidget.current.chart(chartIndex);
          const studiesList = chart.getAllStudies();
          let isVolumeIndicatorFound = false;
          for (
            let studyIndex = 0;
            studyIndex < studiesList.length;
            studyIndex++
          ) {
            const study = studiesList[studyIndex];
            if (study.name === 'Volume') {
              isVolumeIndicatorFound = true;
              break;
            }
          }
          if (!isVolumeIndicatorFound) {
            chart.createStudy('Volume', true);
            tvWidget.current?.saveChartToServer();
          }
        }
        LStorage.setItem('VOLUME_INDICATOR_ADDED', 'true');
      }
    }
  };

  const onLayoutChangedHandler = () => {
    if (tvWidget.current) {
      debouncedCreateAlert(tvWidget.current);
      debouncedHandleUnderlyingChartsOnLayoutChange();
      addVolumeIndicatorOnUpstoxLayout();
    }
  };

  function onDataLoadedHandler() {
    if (symbolChangeStartTime?.current && !isPopup) {
      const elapsedTime = Date.now() - symbolChangeStartTime?.current;
      AnalyticsService.sendEvent(
        EVENTS.SYMBOL_CHANGE,
        { 'Time taken': `${elapsedTime} ms` },
        true,
      );
      symbolChangeStartTime.current = 0;
    }

    if (intervalChangeStartTime?.current && !isPopup) {
      const elapsedTime = Date.now() - intervalChangeStartTime?.current;
      AnalyticsService.sendEvent(
        EVENTS.INTERVAL_CHANGE,
        { 'Time taken': `${elapsedTime} ms` },
        true,
      );
      intervalChangeStartTime.current = 0;
    }

    if (tvWidget.current) {
      const exchangeToken = getExchangeToken();
      // If the exchange token is different from the symbol,
      // then set the symbol to update the route
      if (exchangeToken !== symbol) {
        handleSymbolSelect && handleSymbolSelect(exchangeToken);
      }
      debouncedCreateAlert(tvWidget.current);
    }
  }

  const onSymbolChanged = () => {
    const exchangeToken = getExchangeToken();
    symbolChangeStartTime.current = Date.now();

    // When offline, if user changes scrip, then set symbol with _reload tag
    if (isOfflineRef?.current && tvWidget?.current) {
      // In case of switching scrip when offline full name won't be resolved from symbol search.
      // so taking symbol from chart
      // symbol contains display name by default, so we can't just rely on this
      const tvSymbol =
        tvWidget?.current?.activeChart()?.symbolExt()?.full_name ||
        tvWidget?.current?.activeChart()?.symbol();

      tvWidget.current.setSymbol(
        `${tvSymbol}_reload${Math.round(Math.random() * 1000)}`,
        getChartInterval(),
        () => ({}),
      );
    }

    if (handleSymbolSelect && exchangeToken) {
      if (tvWidget.current) {
        debouncedCreateAlert(tvWidget.current);
      }
      handleSymbolSelect(exchangeToken);
    }

    handleFooterButton();

    handleIsContinuousChartsApplicable();

    handleIsUnderlyingChartsApplicable();

    const activeChartInterval = getChartInterval();
    const tvChartInterval = ChartFormatService.mapTVChartInterval(
      activeChartInterval,
    );

    handleIntervalChangeForContinuousCharts(tvChartInterval);
    // sync with state chart symbol
    setChartSymbol(exchangeToken);
  };

  const onCrossHairLineMove = ({ price }: CrossHairMovedEventParams) => {
    crossHairPrice.current = price.toFixed(2);
  };

  const getExchangeToken = () => {
    const currentSymbol = tvWidget
      ? tvWidget?.current?.activeChart()?.symbolExt()?.full_name
      : '';
    const exchangeToken = Format.decodeCustomTvExchangeToken(
      currentSymbol || '',
    );
    return exchangeToken;
  };

  const onOptionChainClick = useCallback(
    (source = 'Context Menu') => {
      AnalyticsService.sendEvent(EVENTS.OPTION_CHAIN_CLICK, { source }, true);
      (async () => {
        if (tvWidget.current) {
          tvWidget.current.exitFullscreen();
        }
        setIsMinimised(false);
        const exchangeToken = getExchangeToken();
        const isOptionChainModalOpen = AppService.modalsConfig.optionChain;
        if (isOptionChainModalOpen) {
          const scripData = await SearchService.searchByExchangeToken(
            exchangeToken,
          );
          const scripDataExchangeToken =
            scripData?.underlyingExchangeToken || scripData?.exchangeToken;
          const selectedOptionExchangeToken =
            OptionChainService.scripData?.underlyingExchangeToken ||
            OptionChainService.scripData?.exchangeToken;

          if (scripDataExchangeToken !== selectedOptionExchangeToken) {
            unsubscribeOptionChainScrips();
            clearOptionData();
            await setOptionChainData(exchangeToken || '');
          }
        } else {
          await setOptionChainData(exchangeToken || '');
          toggleModal('optionChain');
        }
      })();
    },
    [
      clearOptionData,
      setIsMinimised,
      setOptionChainData,
      toggleModal,
      unsubscribeOptionChainScrips,
    ],
  );

  const onPriceAlertsClick = (
    openCreateModal?: boolean,
    price?: string,
    type?: 'Above' | 'Below',
  ) => {
    const exchangeToken = getExchangeToken();
    SearchService.searchByExchangeToken(exchangeToken)
      .then(data => {
        setScripData(data);
        if (openCreateModal) {
          setModalType('Create');
          if (type === 'Above') {
            setPriceAbove(price || '');
          } else if (type === 'Below') {
            setPriceBelow(price || '');
          }
        } else {
          setModalType('List');
        }
        toggleModal('priceAlerts');
      })
      .catch(err => {
        Logger.log('SEARCH', err);
      });
  };

  const handleIntervalChangeForContinuousCharts = useCallback(
    (tvChartInterval: ServerIntervals, fromIntervalChange = false) => {
      const exchangeToken = getExchangeToken();
      setChartIntervalByExchangeToken(exchangeToken, tvChartInterval);
      const isContinousChartCheckedForSymbolAnd1D =
        isContinousChartChecked[`${exchangeToken}_1d`];

      if (tvChartInterval !== ServerIntervals.ONE_DAY) {
        const token = `${exchangeToken}_${tvChartInterval}`;

        setIsContinousChart(token, false);

        if (
          fromIntervalChange &&
          isContinousChartCheckedForSymbolAnd1D &&
          broker.instance
        ) {
          broker.instance.showNotification(
            LocaleService.translate('CONTINUOUS_CHARTS_INFO'),
            '',
            NotificationType.Success,
          );
        }
      }
    },
    [
      setChartIntervalByExchangeToken,
      setIsContinousChart,
      isContinousChartChecked,
    ],
  );

  const handleIntervalChanged = useCallback(
    (int: ResolutionString) => {
      intervalChangeStartTime.current = Date.now();

      const mappedInterval = ChartFormatService.getUserPeriodByChartInterval(
        int as TimeIntervals,
      );

      // Mixpanel for custom interval selected
      if (!CHART_TO_USER_INTERVAL[int as keyof typeof CHART_TO_USER_INTERVAL]) {
        const intervalType =
          INTERVAL_TYPE[mappedInterval.slice(-1) as keyof typeof INTERVAL_TYPE];
        const intervalValue = mappedInterval.slice(0, -1);

        AnalyticsService.sendEvent(EVENTS.CUSTOM_INTERVAL_ADDED, {
          'Interval type': intervalType,
          'Interval value': intervalValue,
        });
      }

      onIntervalChanged(mappedInterval);

      const tvChartInterval = ChartFormatService.mapTVChartInterval(
        mappedInterval,
      );

      handleIntervalChangeForContinuousCharts(tvChartInterval, true);
    },
    [onIntervalChanged, handleIntervalChangeForContinuousCharts],
  );

  const handleSwitchToNewTab = useCallback(() => {
    const exchangeToken = getExchangeToken();
    goToRoute('/charts/:id', {
      params: {
        id: exchangeToken,
      },
      query: {
        isPopup: true,
      },
      inNewTab: true,
    });
  }, [goToRoute]);

  useEffect(() => {
    // Maintaining ref for isOffline to get updated value in onSymbolChanged callback
    if (isOfflineRef) {
      isOfflineRef.current = isOffline;
    }
  }, [isOffline]);

  useEffect(() => {
    if (
      shouldResetChart &&
      shouldResetChart !== prevShouldResetChart &&
      tvWidget.current &&
      isWidgetReady
    ) {
      TradingChartService.resetCharts();
      resetCharts(tvWidget.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldResetChart, prevShouldResetChart, isWidgetReady]);

  /**
   * useEffect to reload the appropriate chart layout in two scenarios:
   * 1. When the current layout is deleted, ensuring the user always has a valid layout loaded.
   * 2. When auto-sync is completed, updating the layout to reflect the latest changes from the server.
   * The function first attempts to find and load the current layout.
   * If unavailable, it falls back to loading the default Upstox Layout.
   */
  useEffect(() => {
    const loadChartFromServer = (savedCharts: SaveLoadChartRecord[]) => {
      // Find the current saved layout or fall back to the Upstox layout
      const savedChart = savedCharts.find(
        chart => chart.id === currentLayoutId,
      ) || savedCharts.find(chart => chart.id === upstoxLayoutId);
      if (savedChart) {
        tvWidget?.current?.loadChartFromServer(savedChart);
      }
    };
    tvWidget?.current?.getSavedCharts(loadChartFromServer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadLayout]);

  const resetCharts = (tvChartWidget: IChartingLibraryWidget) => {
    TradingChartService.resetCharts();
    const chartsCount = tvChartWidget.chartsCount();
    for (let index = 0; index < chartsCount; index++) {
      resetChartData(tvChartWidget, index);
    }
  };

  const resetChartData = (
    tvChartWidget: IChartingLibraryWidget,
    index: number,
  ) => {
    tvChartWidget.chart(index).resetData();

    // In case of switching scrip when offline full name won't be resolved from symbol search.
    // so taking symbol from chart
    // symbol contains display name by default, so we can't just rely on this
    const tvSymbol =
      tvChartWidget?.chart(index)?.symbolExt()?.full_name ||
      tvChartWidget?.chart(index)?.symbol();

    tvChartWidget
      .chart(index)
      .setSymbol(`${tvSymbol}_reload${Math.round(Math.random() * 1000)}`);
    };

  const handleTbtFooterClick = useCallback(() => {
    AppService.toggleModal('tbtVideoModal');
  }, []);

  const isTBTApplicable = () => {
    const exchangeToken = getExchangeToken();
    const { segment } = decomposeExchangeToken(exchangeToken);
    const isTbtApplicable = [
      SegmentType.NSE_EQ,
      SegmentType.NSE_FO,
      SegmentType.NCD_FO,
    ].includes(segment);
    return isTbtApplicable;
  };

  const handleIsContinuousChartsApplicable = async () => {
    const exchangeToken = getExchangeToken();

    const symbolDescription = tvWidget?.current?.activeChart().symbolExt()
      ?.description;
    if (symbolDescription?.includes('FUT')) {
      const result = await SearchService.searchByExchangeToken(exchangeToken);

      const expiries = await SearchService.getOptionExpiries(
        exchangeToken,
        result?.underlyingExchangeToken || '',
      );
      const monthlyExpiries = expiries.filter(e => !e.isWeekly);
      const scripExpiryMonth = Number(result?.expiryMonth);

      const currentMonthlyExpiryTimestamp = monthlyExpiries?.[0]?.date;
      const currentExpiryMonth = new Date(
        currentMonthlyExpiryTimestamp,
      ).getMonth();

      const nextMonthlyExpiryTimestamp = monthlyExpiries[1]?.date;
      const nextExpiryMonth = new Date(nextMonthlyExpiryTimestamp).getMonth();

      const isApplicableForMonth =
        scripExpiryMonth === currentExpiryMonth + 1 ||
        scripExpiryMonth === nextExpiryMonth + 1;

      const { segment } = decomposeInstrumentKey(result?.instrumentKey || '');
      const isNSEFuture =
        result?.instrument === 'FUT' && segment === SegmentType.NSE_FO;

      if (isApplicableForMonth && isNSEFuture) {
        const number = scripExpiryMonth === currentExpiryMonth + 1 ? 1 : 2;
        const futuresToken = `${result.underlyingSymbol}_FUT${number}`;
        setIsContinuousChartsApplicable(exchangeToken, true);

        setContinousChartInstrumentKey(
          exchangeToken,
          `${segment}|${futuresToken}` || '',
        );
        return;
      }
    }

    setIsContinuousChartsApplicable(exchangeToken, false);
  };

  const handleIsUnderlyingChartsApplicable = async () => {
    const exchangeToken = getExchangeToken();

    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    const isUnderlyingChartsApplicableForCurrentSymbol =
      !!scripData?.segment &&
      ValidationService.isFUTorOPT(scripData?.instrument) &&
      !ValidationService.isCurrency(scripData?.segment);
    setIsUnderlyingChartsApplicable(
      exchangeToken,
      isUnderlyingChartsApplicableForCurrentSymbol,
    );
  };

  const toolbarObserverCallback = (
    dateRangeWrapperList: NodeListOf<Element>,
  ) => {
    dateRangeWrapperList.forEach(dateRangeWrapper => {
      const isCollapsed = dateRangeWrapper?.classList?.value?.includes(
        'collapsed',
      );
      if (!isCollapsed) {
        dateRangeWrapper.setAttribute(
          'style',
          'position: relative; display: flex',
        );
      } else {
        dateRangeWrapper.setAttribute('style', 'position: absolute');
      }
    });
  };

  const createPositionPnlElement = () => {
    const element = document.createElement('div');
    element.id = 'position-pnl';
    element.style.cssText =
      'margin-left: 10px; font-weight: normal; align-items: center; display: flex;';
    return element;
  };

  const createTbtLogoButton = (isTBTEnabled: boolean) => {
    const svgWrapper = document.createElement('div');
    svgWrapper.innerHTML = tbtFooterButtonSvg(isTBTEnabled);
    svgWrapper.setAttribute(
      'style',
      'display: flex; align-items: center; cursor: pointer;',
    );
    svgWrapper.addEventListener('click', handleTbtFooterClick);
    return svgWrapper;
  };

  const createTbtFooter = (isTBTEnabled: boolean) => {
    const toolbarWrapper = document
      .querySelector('iframe')
      ?.contentDocument?.querySelectorAll(
        '[class="chart-toolbar chart-controls-bar"]',
      );
    const toolbar = toolbarWrapper
      ? toolbarWrapper[0]?.querySelector('[class^="toolbar"]')
      : null;

    if (toolbar) {
      toolbar.setAttribute(
        'style',
        'display:flex; overflow-x:auto; overflow-y: hidden',
      );
      const seriesWrapper = toolbar.querySelectorAll(
        '[ class^="seriesControlWrapper" ]',
      )[0];
      seriesWrapper.setAttribute(
        'style',
        'display: flex; justify-content: flex-end; flex: 1; position: relative',
      );

      const dateRangeWrapper = toolbar.querySelectorAll(
        '[ class^="dateRangeWrapper" ]',
      );

      if (PlatformService.isMobile) {
        if (toolbarObserver.current) {
          toolbarObserver.current.disconnect();
          toolbarObserver.current = null;
        }
        toolbarObserver.current = new MutationObserver(() =>
          toolbarObserverCallback(dateRangeWrapper),
        );
        toolbarObserver.current.observe(toolbar, TOOLBAR_OBSERVER_CONFIG);

        const footerWrapper = seriesWrapper.insertBefore(
          document.createElement('div'),
          seriesWrapper.firstChild,
        );
        footerWrapper.setAttribute(
          'style',
          'display: inline-block; height: 100%; vertical-align: top;',
        );

        tbtFooterButton.current = footerWrapper.appendChild(
          document.createElement('div'),
        );
        tbtFooterButton.current.setAttribute(
          'style',
          'height: 100%; align-items: center; display: flex; line-height: 21px; padding: 0px 6px;',
        );
        tbtFooterButton.current.setAttribute('id', 'tbt-footer-button');

        // Powered By TBT svg
        tbtFooterButton.current.appendChild(createTbtLogoButton(isTBTEnabled));
      } else {
        const dateRangeTbtWrapper = toolbar.insertBefore(
          document.createElement('div'),
          toolbar.firstChild,
        );
        dateRangeTbtWrapper.setAttribute('style', 'display: flex;');
        dateRangeTbtWrapper.appendChild(dateRangeWrapper[0]);
        dateRangeTbtWrapper.appendChild(dateRangeWrapper[1]);
        dateRangeWrapper[0].setAttribute('style', 'display: none');
        dateRangeWrapper[1].setAttribute(
          'style',
          'position: relative; display: flex; visibility: visible;',
        );

        tbtFooterButton.current = dateRangeTbtWrapper.appendChild(
          document.createElement('div'),
        );
        tbtFooterButton.current.setAttribute(
          'style',
          'position: relative;height: 100%; align-items: center; display: flex; line-height: 21px; margin-left: 7px; margin-right: 4px',
        );
        tbtFooterButton.current.setAttribute('id', 'tbt-footer-button');

        // Powered By TBT svg
        tbtFooterButton.current.appendChild(createTbtLogoButton(isTBTEnabled));

        // Append position pnl element to tbtFooterButton
        positionPnlElement.current = createPositionPnlElement();
        tbtFooterButton.current.appendChild(positionPnlElement.current);
        // Forcefully re render the pnl component by changing the state
        setForceUpdatePositionPnl(prev => !prev);
      }
    }
  };

  // Also add cleanup in component unmount
  useEffect(
    () => () => {
      if (toolbarObserver.current) {
        toolbarObserver.current.disconnect();
        toolbarObserver.current = null;
      }
    },
    [],
  );

  const handleTbtFooterButton = () => {
    const tbtButton = document
      .querySelector('iframe')
      ?.contentDocument?.querySelectorAll('[id="tbt-footer-button"]');
    if (tbtButton?.length) {
      tbtButton[0].remove();
      if (toolbarObserver.current) {
        toolbarObserver.current.disconnect();
      }
    }

    const isTBTEnabled = isTBTApplicable();
    footerButtonTimeout.current = setTimeout(() => {
      createTbtFooter(isTBTEnabled);
    }, 0);
  };

  const handleFooterButton = () => {
    if (footerButtonTimeout?.current) {
      clearTimeout(footerButtonTimeout?.current);
    }
    footerButtonTimeout.current = setTimeout(() => {
      handleTVChartSyncButton();
      handleTbtFooterButton();
    }, 0);
  };

  const handleTVChartSyncClick = useCallback(() => {
    if (tvWidget && saveChartsToServer) {
      const loadChartFromServer = (savedCharts: SaveLoadChartRecord[]) => {
        // Current chart layout name
        const currentLayoutName = tvWidget?.current?.layoutName();
        const savedChart = savedCharts.find(
          chart => chart.name === currentLayoutName,
        );
        if (savedChart) {
          tvWidget?.current?.loadChartFromServer(savedChart);
          if (isUnderlyingChartsCheckedRef.current) {
            UnderlyingChartsService.toggleUnderlyingCharts();
            UnderlyingChartsService.clearUnderlyingChartsList();
          }
        }
      };
      tvWidget?.current?.getSavedCharts(loadChartFromServer);
    }
  }, [tvWidget, saveChartsToServer]);

  const createTVChartSyncButton = useCallback(() => {
    const toolbarWrapper = document
      .querySelector('iframe')
      ?.contentDocument?.querySelectorAll(
        '[class="chart-toolbar chart-controls-bar"]',
      );
    const toolbar = toolbarWrapper
      ? toolbarWrapper[0]?.querySelector('[class^="toolbar"]')
      : null;
    const { tvChartSyncButtonSvg } = getActionsButtons();

    if (toolbar) {
      const dateRangeWrapper = toolbar.querySelectorAll(
        '[ class^="dateRangeWrapper" ]',
      );
      toolbar.setAttribute(
        'style',
        'display:flex; overflow-x:auto; overflow-y: hidden',
      );
      tvChartSyncButton.current = document.createElement('div');

      tvChartSyncButton.current.innerHTML = tvChartSyncButtonSvg;
      tvChartSyncButton.current.setAttribute(
        'style',
        'height: 100%; width: 24px; display: flex; vertical-align: middle; cursor: pointer',
      );
      tvChartSyncButton.current.addEventListener(
        'click',
        handleTVChartSyncClick,
      );
      tvChartSyncButton.current.setAttribute('id', 'tv-chart-sync-button');

      if (dateRangeWrapper && tvChartSyncButton) {
        if (PlatformService.isMobile) {
          dateRangeWrapper[0].appendChild(tvChartSyncButton?.current);
          dateRangeWrapper[0].setAttribute(
            'style',
            'display: flex; position: relative',
          );
        } else {
          dateRangeWrapper[1].appendChild(tvChartSyncButton?.current);
          dateRangeWrapper[1].setAttribute(
            'style',
            'display: flex; position: relative',
          );
        }
      }
    }
  }, [handleTVChartSyncClick]);

  // Cleanup
  useEffect(
    () => () => {
      if (tvChartSyncButton.current) {
        tvChartSyncButton.current.removeEventListener(
          'click',
          handleTVChartSyncClick,
        );
      }
    },
    [handleTVChartSyncClick],
  );

  const handleTVChartSyncButton = useCallback(() => {
    const tvChartSyncButtonElement = document
      .querySelector('iframe')
      ?.contentDocument?.querySelectorAll('[id="tv-chart-sync-button"]');
    if (tvChartSyncButtonElement && tvChartSyncButtonElement?.length) {
      tvChartSyncButtonElement[0].remove();
    }

    if (saveChartsToServerRef?.current) {
      createTVChartSyncButton();
    }
  }, [createTVChartSyncButton, saveChartsToServerRef]);

  useEffect(() => {
    if (saveChartsToServerRef) {
      // eslint-disable-next-line max-len
      saveChartsToServerRef.current = !!saveChartsToServer;
    }

    handleTVChartSyncButton();
  }, [
    userPreferenceSettings,
    handleTVChartSyncButton,
    syncingInProgress,
    prevSaveChartsToServer,
    saveChartsToServer,
  ]);

  const handleContinuousChartsButton = () => {
    if (tvWidget?.current) {
      continuousChartsButton.current = tvWidget.current.createButton({
        align: 'left',
        useTradingViewStyle: false,
      });

      continuousChartsButton.current.setAttribute('id', 'continuous-futures');

      continuousChartsButton.current.setAttribute('style', baseStyle);

      const activeChartInterval = getChartInterval();
      const tvChartInterval = ChartFormatService.mapTVChartInterval(
        activeChartInterval,
      );

      const exchangeToken = getExchangeToken();

      setChartIntervalByExchangeToken(exchangeToken, tvChartInterval);

      handleIsContinuousChartsApplicable();
    }
  };

  const handleUnderlyingChartsButton = () => {
    if (tvWidget?.current) {
      underlyingChartsButton.current = tvWidget.current.createButton({
        align: 'left',
        useTradingViewStyle: false,
      });

      underlyingChartsButton.current.setAttribute('id', 'underlying-charts');

      underlyingChartsButton.current.setAttribute('style', baseStyle);

      handleIsUnderlyingChartsApplicable();
    }
  };

  const debouncedCreateAlert = useCallback(debounce(createPriceAlert, 1000), [
    createPriceAlert,
  ]);

  const debouncedHandleUnderlyingChartsOnLayoutChange = useCallback(
    debounce(() => {
      if (tvWidget.current && isUnderlyingChartsCheckedRef.current) {
        handleUnderlyingChartsOnLayoutChange(tvWidget.current);
      }
    }, 1000),
    [handleUnderlyingChartsOnLayoutChange],
  );

  useEffect(() => {
    if (tvWidget.current) {
      debouncedCreateAlert(tvWidget.current);
    }
  }, [
    priceAlertListData,
    debouncedCreateAlert,
  ]);

  const insertPriceAlertButton = () => {
    if (tvWidget?.current) {
      priceAlertButton.current = tvWidget.current.createButton({
        align: 'left',
        useTradingViewStyle: false,
      });

      priceAlertButton.current.setAttribute('title', 'Price Alerts');
      priceAlertButton.current.setAttribute('data-id', 'price-alerts-button');
      priceAlertButton.current.innerHTML = `${getPriceAlertButton()} Alert`;
      priceAlertButton.current.setAttribute('style', baseStyle);
      priceAlertButton.current.addEventListener(
        'click',
        handlePriceAlertsClick,
      );
    }
  };

  const handlePriceAlertsClick = () => {
    onPriceAlertsClick(false);
  };

  const handleAddFundsClick = useCallback(() => {
    if (handleAddFunds) {
      handleAddFunds();
    }
  }, [handleAddFunds]);

  const dropdownItems = useMemo(
    () => [
      {
        title: 'Review Order',
        onSelect: () => toggleModal('orderReview'),
        enabled: true,
      },
      {
        title: 'Dark/Light Theme',
        onSelect: () => toggleModal('changeTheme'),
        enabled: true,
      },
      {
        title: 'Save settings',
        onSelect: () => toggleModal('chartSaveSettings'),
        enabled: true,
      },
      {
        title: 'Help',
        onSelect: () => handleHelp && handleHelp(),
        enabled: true,
      },
      {
        title: 'Important links',
        onSelect: () => toggleModal('importantLinks'),
        enabled: true,
      },
      {
        title: 'Logout',
        onSelect: () => handleLogout && handleLogout(),
        enabled: true,
      },
    ],
    [handleHelp, handleLogout, toggleModal],
  );

  const initializeChart = useCallback(async () => {
    // Move recent layout to saved layout
    await saveLoadLayoutAdapter.moveRecentLayoutToSavedLayout();
    tvWidget.current = new Widget({
      ...getWidgetOptions(),
      load_last_chart: true,
    });

    try {
      await tvWidget.current?.headerReady();

      if (!isPopup) {
        switchTabButton.current = tvWidget.current.createButton({
          align: 'left',
          useTradingViewStyle: false,
        });
        switchTabButton.current.setAttribute('title', 'Open in a new tab');
        switchTabButton.current.setAttribute('data-id', 'OpenChartInNewTab');

        switchTabButton.current.innerHTML = switchTabButtonElement;
        switchTabButton.current.setAttribute('style', popoutStyle);

        switchTabButton.current.addEventListener('click', handleSwitchToNewTab);
      }

      tvWidget.current
        .createDropdown({
          title: '',
          items: dropdownItems.filter(item => item.enabled),
          align: 'right',
          icon: menuButtonElement(getName()),
        })
        .then(dropdown => {
          menuButton.current = dropdown;
        });

      if (handleAddFunds) {
        addFundsButton.current = tvWidget.current.createButton({
          align: 'left',
          useTradingViewStyle: false,
        });

        addFundsButton.current.setAttribute('title', 'Add Funds');
        addFundsButton.current.setAttribute('data-id', 'add-funds-button');

        addFundsButton.current.innerHTML = addFundsButtonSvg(isDarkTheme);

        addFundsButton.current.setAttribute('style', baseStyle);

        addFundsButton.current.addEventListener('click', handleAddFundsClick);
      }

      if (window?.screen?.width >= OptionChainModalMinWidth) {
        optionChainButton.current = tvWidget.current.createButton({
          align: 'left',
          useTradingViewStyle: false,
        });
        optionChainButton.current.setAttribute('title', 'Option Chain');
        optionChainButton.current.setAttribute(
          'data-id',
          'option-chain-button',
        );
        optionChainButton.current.innerHTML = getOptionChainButton();
        optionChainButton.current.setAttribute('style', baseStyle);
        optionChainButton.current.addEventListener(
          'click',
          handleOptionChainClick,
        );
      }

      if (!PlatformService.isMobile) {
        insertPriceAlertButton();
      }

      if (!PlatformService.isMobile) {
        handleUnderlyingChartsButton();
      }

      handleContinuousChartsButton();

      handleFooterButton();

      handleRightToolbarDOMButton();

      tvWidget.current.activeChart().dataReady(() => {
        const startTime = parseInt(
          CookieStorage.getItem('app_load_start_time') as string,
          10,
        );
        if (startTime) {
          const elapsedTime = Date.now() - startTime;
          AnalyticsService.sendEvent(
            EVENTS.PIN_TO_TV_CHART_LOAD_TIME,
            { 'Time taken': `${elapsedTime} ms` },
            true,
          );
          CookieStorage.removeItem('app_load_start_time', cookieConfig);
        }
      });

      tvWidget.current
        .activeChart()
        .onDataLoaded()
        .subscribe(null, onDataLoadedHandler, false);

      setLoadingState(false);
    } catch (error) {
      // handle header ready errors
    }

    tvWidget.current.onChartReady(() => {
      setWidgetReady(true);
      try {
        if (!tvWidget.current) return;
        addVolumeIndicatorOnUpstoxLayout();
        activeChart.current = tvWidget.current?.activeChart();
        if ((isFromPW3 || isPopup === 'true') && symbol) {
          const setSymbolInChartLayout = async () => {
            const scripData = await SearchService
              .searchByExchangeToken(symbol);
            const tvExchangeToken = Format.generateCustomTvExchangeToken(
              scripData?.displayName || '',
              scripData?.exchangeToken || '',
            );
            if (tvWidget.current) {
              tvWidget.current
                .setSymbol(tvExchangeToken, getChartInterval(), () => ({}));
            }
          };
          setSymbolInChartLayout();
        }
        tvWidget.current.subscribe('activeChartChanged', () => {
          onSymbolChanged();

          // unsubscribe from previous active chart changes
          if (activeChart.current) {
            activeChart.current
              .onSymbolChanged()
              .unsubscribe(null, onSymbolChanged);

            activeChart.current
              .onIntervalChanged()
              .unsubscribe(null, handleIntervalChanged);

            activeChart.current
              .crossHairMoved()
              .unsubscribe(null, onCrossHairLineMove);
          }

          activeChart.current = tvWidget.current?.activeChart();

          if (activeChart.current) {
            activeChart.current
              .onSymbolChanged()
              .subscribe(null, onSymbolChanged);

            activeChart.current
              .onIntervalChanged()
              .subscribe(null, handleIntervalChanged);

            activeChart.current
              .crossHairMoved()
              .subscribe(null, onCrossHairLineMove);
          }
        });

        tvWidget.current.onContextMenu(() => {
          const options = [
            { text: '-Object Tree...' },
            {
              text: 'Option Chain',
              position: 'top',
              click: () => onOptionChainClick('Context Menu'),
            },
            {
              text: 'Create Alert',
              position: 'top',
              click: () => onPriceAlertsClick(true),
            },
          ];

          return options as ContextMenuItem[];
        });

        // parent subscriptions
        tvWidget.current.subscribe('layout_changed', onLayoutChangedHandler);

        tvWidget.current.subscribe('study', studyHandler);

        // watchlist subscription
        if (!isPopup) {
          tvWidget.current.watchList().then(newApi => {
            new WatchlistService(newApi).fetchWatchlists();
          });
        }
      } catch (error) {
        // some async initialization error occurs
      }

      // shared subscriptions
      if (tvWidget.current) {
        tvWidget.current
          .activeChart()
          .onIntervalChanged()
          .subscribe(null, handleIntervalChanged);

        // eslint-disable-next-line no-unused-expressions
        tvWidget.current
          ?.activeChart()
          .onSymbolChanged()
          .subscribe(null, onSymbolChanged);

        // eslint-disable-next-line no-unused-expressions
        tvWidget.current
          ?.activeChart()
          ?.crossHairMoved()
          .subscribe(null, onCrossHairLineMove);
      }
    });

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getWidgetOptions,
    handleIntervalChanged,
    handleAddFunds,
    dropdownItems,
    handleRightToolbarDOMButton,
  ]);

  const handleOptionChainClick = useCallback(() => {
    onOptionChainClick('Header');
  }, [onOptionChainClick]);

  const initializeChartIfNeeded = useCallback(() => {
    if (!isInitialized && symbol) {
      initializeChart();
    }
  }, [initializeChart, isInitialized, symbol]);

  useEffect(() => {
    if (saveChartsToServer && menuButton?.current) {
      menuButton.current.applyOptions({
        items: dropdownItems.filter(item => item.enabled),
      });
    }
  }, [saveChartsToServer, menuButton, dropdownItems]);

  useEffect(() => {
    // Initialize widget instance reference
    let widgetInstance = tvWidget.current;

    // Function to update widget instance reference
    const updateWidgetInstance = () => {
      if (tvWidget.current !== widgetInstance) {
        widgetInstance = tvWidget.current;
      }
    };

    // Initial update of widget instance
    updateWidgetInstance();

    const cleanup = () => {
      Logger.log('APP', 'unmounting chart');

      if (widgetInstance) {
        try {
          widgetInstance.changeTheme(isDarkTheme ? 'dark' : 'light');

          // Unsubscribe from all events
          widgetInstance.unsubscribe(
            'onAutoSaveNeeded',
            onAutoSaveNeededHandler,
          );
          widgetInstance.unsubscribe('layout_changed', onLayoutChangedHandler);
          widgetInstance.unsubscribe('chart_loaded', onLayoutChangedHandler);
          widgetInstance.unsubscribe('study', studyHandler);

          const currentActiveChart = widgetInstance.activeChart();
          if (currentActiveChart) {
            currentActiveChart
              .onDataLoaded()
              .unsubscribe(null, onDataLoadedHandler);
            currentActiveChart
              .onIntervalChanged()
              .unsubscribe(null, handleIntervalChanged);
            currentActiveChart
              .onSymbolChanged()
              .unsubscribe(null, onSymbolChanged);
            currentActiveChart
              .crossHairMoved()
              .unsubscribe(null, onCrossHairLineMove);
          }

          // Clear timeouts
          if (changeThemeTimeout.current) {
            clearTimeout(changeThemeTimeout.current);
          }
          if (footerButtonTimeout.current) {
            clearTimeout(footerButtonTimeout.current);
          }

          // Clear observers
          if (toolbarObserver.current) {
            toolbarObserver.current.disconnect();
          }

          // Remove DOM event listeners
          if (tbtFooterButton.current) {
            tbtFooterButton.current.removeEventListener(
              'click',
              handleTbtFooterClick,
            );
          }
          if (priceAlertButton.current) {
            priceAlertButton.current.removeEventListener(
              'click',
              handlePriceAlertsClick,
            );
          }
          if (switchTabButton.current) {
            switchTabButton.current.removeEventListener(
              'click',
              handleSwitchToNewTab,
            );
          }
          if (optionChainButton.current) {
            optionChainButton.current.removeEventListener(
              'click',
              handleOptionChainClick,
            );
          }
          if (addFundsButton.current) {
            addFundsButton.current.removeEventListener(
              'click',
              handleAddFundsClick,
            );
          }

          // Finally remove the widget
          widgetInstance.remove();
          widgetInstance = null; // Clear the reference
        } catch (error) {
          Logger.error('Cleanup error:', error);
        }
      }
    };

    const handleBeforeUnload = () => {
      cleanup();
    };

    // Add beforeunload event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Initialize chart if not loading
    if (!isAppLoading) {
      initializeChartIfNeeded();
      // Update widget instance after initialization
      updateWidgetInstance();
    }

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanup();
    };
    // this effect should run only for mount and unmount
    // and only after App has been initialized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppLoading]);

  return (
    <React.Fragment>
      {showLoading && (
        <View css={css.loading} alignItems="center" justifyContent="center">
          <Loading />
        </View>
      )}

      {edisStore.EDISVerificationStatus === 'pending' && <Loader />}

      <View flex id="tv_chart_container" css={css.wrapper} />
      <ChartDocumentTitle />
      <UpdatePositionPnl ref={positionPnlElement} />
      <AveragePriceUpdateNotificationFooter />
    </React.Fragment>
  );
};

export default observer(TradingViewChart);
